import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, Badge, Button } from '../../components/ui/Kit';
import Spinner from '../../components/ui/Spinner';
import EmptyState from '../../components/ui/EmptyState';
import { useToast } from '../../components/ui/Toast';
import { api } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { getPmEventPreferredLiveUrl } from '../../lib/pmEventUtils';
import InviteLinkBlock from './InviteLinkBlock';
import { isPipelineParticipant, isPipelineLeadership } from './pipelineUtils';
import SessionTimes from '../../components/Mentors/SessionTimes';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiBookOpen, FiExternalLink } = FiIcons;

const COMPLETED_EVENT_GRACE_MS = 24 * 60 * 60 * 1000;

/** Latest ends_at, else latest starts_at; null if no sessions / no usable times (keep visible). */
function getEventEffectiveEndMs(event) {
  const sessions = Array.isArray(event?.sessions) ? event.sessions : [];
  if (!sessions.length) return null;

  const endTimes = sessions
    .map((s) => (s?.ends_at ? new Date(s.ends_at).getTime() : null))
    .filter((t) => t != null && Number.isFinite(t));
  if (endTimes.length) return Math.max(...endTimes);

  const startTimes = sessions
    .map((s) => (s?.starts_at ? new Date(s.starts_at).getTime() : null))
    .filter((t) => t != null && Number.isFinite(t));
  if (startTimes.length) return Math.max(...startTimes);

  return null;
}

function isEventCompletedPastGrace(event, nowMs = Date.now()) {
  const endMs = getEventEffectiveEndMs(event);
  if (endMs == null) return false;
  return endMs < nowMs - COMPLETED_EVENT_GRACE_MS;
}

function statusVariant(status) {
  if (status === 'published') return 'success';
  if (status === 'archived') return 'default';
  return 'warning';
}

function eventKindLabel(type) {
  if (type === 'business_launch') return 'Business Launch';
  if (type === 'free_summary_class') return 'Free Summary Class';
  return 'Event';
}

function EventCardThumb({ url, title }) {
  const [broken, setBroken] = useState(false);
  const showImage = Boolean(url) && !broken;

  return (
    <div className="relative h-36 w-full overflow-hidden bg-navy">
      {showImage ? (
        <img
          src={url}
          alt=""
          width={360}
          height={144}
          loading="lazy"
          decoding="async"
          className="h-full w-full object-cover"
          onError={() => setBroken(true)}
        />
      ) : (
        <div className="h-full w-full flex items-end p-4 bg-gradient-to-br from-navy to-[#163a66]">
          <p className="text-white font-heading font-extrabold text-lg leading-snug line-clamp-3">
            {title || 'Event'}
          </p>
        </div>
      )}
    </div>
  );
}

function EventCard({ event, userId, featured = false, onInviteUpdated }) {
  const session = (event?.sessions || [])[0];
  const landingUrl = getPmEventPreferredLiveUrl(event);

  return (
    <Card className={`overflow-hidden flex flex-col ${featured ? 'ring-2 ring-primary/40' : ''}`}>
      <EventCardThumb url={event.hero_image_url} title={event.title} />
      <div className="p-5 space-y-4 flex-1 flex flex-col">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            {featured && (
              <p className="text-[10px] font-bold uppercase tracking-[1.2px] text-amber mb-1">Featured</p>
            )}
            <p className="text-[10.5px] font-heading font-bold uppercase tracking-[1.3px] text-primary mb-1">
              {eventKindLabel(event.pipeline_event_type)}
            </p>
            <h2 className="text-[15.5px] font-heading font-extrabold text-navy leading-snug">
              {event.title}
            </h2>
            {session?.starts_at && (
              <SessionTimes
                startsAt={session.starts_at}
                endsAt={session.ends_at}
                timezone={session.timezone}
                className="mt-1"
              />
            )}
            {event.agent?.full_name && (
              <p className="text-[11.5px] text-slateText mt-1">Hosted by {event.agent.full_name}</p>
            )}
          </div>
          <Badge variant={statusVariant(event.status)}>{event.status || 'draft'}</Badge>
        </div>

        {landingUrl && (
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            onClick={() => window.open(landingUrl, '_blank', 'noopener,noreferrer')}
          >
            <SafeIcon icon={FiExternalLink} className="w-3.5 h-3.5 mr-2" />
            View Landing Page
          </Button>
        )}

        <div className="mt-auto space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-slateText">Your Invite Link</p>
          <InviteLinkBlock
            event={event}
            userId={userId}
            invite={event.invite}
            onGenerated={onInviteUpdated}
          />
        </div>
      </div>
    </Card>
  );
}

export default function AcademyHub() {
  const { user } = useAuth();
  const { addToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [teamIds, setTeamIds] = useState([]);

  const canView = isPipelineParticipant(user?.role) || isPipelineLeadership(user?.role);

  const load = useCallback(async () => {
    if (!user?.id) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      let nextTeamIds = [];
      if (isPipelineLeadership(user?.role)) {
        const team = await api.users.getMyTeam();
        nextTeamIds = (team || []).map((u) => u.id);
        setTeamIds(nextTeamIds);
      } else {
        setTeamIds([]);
      }

      const list = (await api.pipelineEvents.getForAssociate(user.id, null)) || [];
      // Hide events whose last session ended >24h ago (Academy Hub only; Mentors list unchanged).
      const active = list.filter((row) => !isEventCompletedPastGrace(row));
      const detailed = await Promise.all(
        active.map(async (row) => {
          const [invite, full] = await Promise.all([
            api.pipelineEvents.getInvitationLink(row.id, user.id),
            api.pmEvents.getById(row.id).catch(() => null),
          ]);
          const { invitation_links: _agencyInvites, ...eventFields } = full || {};
          return {
            ...row,
            ...eventFields,
            invite: invite ?? null,
          };
        }),
      );
      setEvents(detailed);
    } catch (err) {
      console.error(err);
      addToast({ type: 'error', title: 'Could not load Academy Hub', message: err.message || 'Try again.' });
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [user?.id, user?.role, addToast]);

  useEffect(() => {
    if (!canView) return;
    load();
  }, [canView, load]);

  const { featured, rest } = useMemo(() => {
    if (!events.length) return { featured: [], rest: [] };

    const isFeatured = (event) => {
      const agentId = event?.agent_id || event?.agent?.id;
      if (!agentId) return false;
      if (isPipelineParticipant(user?.role)) {
        return agentId === user.id;
      }
      if (isPipelineLeadership(user?.role)) {
        return teamIds.includes(agentId);
      }
      return false;
    };

    const featuredRows = [];
    const restRows = [];
    for (const event of events) {
      if (isFeatured(event)) featuredRows.push(event);
      else restRows.push(event);
    }
    return { featured: featuredRows, rest: restRows };
  }, [events, user?.id, user?.role, teamIds]);

  if (!canView) {
    return (
      <Card className="p-8 max-w-xl">
        <h1 className="text-xl font-heading font-bold text-navy">Academy Hub</h1>
        <p className="text-sm text-slateText mt-3">This page is available to pipeline participants and leadership.</p>
      </Card>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 gap-3">
        <Spinner size="lg" className="text-primary" />
        <p className="text-slateText text-sm">Loading Academy Hub…</p>
      </div>
    );
  }

  const updateInvite = async (eventId) => {
    const persisted = await api.pipelineEvents.getInvitationLink(eventId, user.id);
    if (!persisted?.id || !persisted?.slug) return;
    setEvents((prev) =>
      prev.map((ev) => (ev.id === eventId ? { ...ev, invite: persisted } : ev)),
    );
  };

  const renderGrid = (rows, featuredFlag) => (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
      {rows.map((event) => (
        <EventCard
          key={event.id}
          event={event}
          userId={user.id}
          featured={featuredFlag}
          onInviteUpdated={() => updateInvite(event.id)}
        />
      ))}
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-heading font-extrabold text-navy tracking-tight">
            Prosperity <span className="text-primary">Academy</span>
          </h1>
          <p className="text-slateText text-sm mt-1">
            Invite a family to a published event with your personal link.
          </p>
        </div>
        <div className="self-start rounded-[100px] border border-lineGray bg-white px-3.5 py-2 text-xs font-semibold text-slateText">
          System 2
        </div>
      </div>

      <div className="relative overflow-hidden rounded-[18px] bg-navy text-white px-6 py-8 md:px-9">
        <div
          className="pointer-events-none absolute -right-10 -bottom-16 h-56 w-56 rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(0,170,255,0.22), transparent 70%)' }}
        />
        <p className="relative text-[11.5px] font-bold uppercase tracking-[1.6px] text-skyTint">Open to All Associates</p>
        <h2 className="relative text-2xl font-heading font-extrabold mt-1.5 max-w-lg leading-snug">
          Share your invite link — no license or launch required.
        </h2>
        <p className="relative text-[13.3px] text-[#a9c0e6] mt-2 max-w-md leading-relaxed">
          Every published agency event is here. Generate a personal invite link and send people to the landing page.
        </p>
      </div>

      {events.length === 0 ? (
        <Card className="p-2">
          <EmptyState
            icon={FiBookOpen}
            title="No events scheduled yet"
            description="When your agency publishes an event, it will show up here with your invite link."
          />
        </Card>
      ) : (
        <>
          {featured.length > 0 && (
            <div className="space-y-3">
              <div>
                <p className="text-[10.5px] font-heading font-bold uppercase tracking-[1.3px] text-primary mb-1">
                  Featured
                </p>
                <h2 className="text-[15.5px] font-heading font-extrabold text-navy">
                  {isPipelineLeadership(user?.role) ? 'Your team\'s events' : 'Your events'}
                </h2>
              </div>
              {renderGrid(featured, true)}
            </div>
          )}
          {rest.length > 0 && (
            <div className="space-y-3">
              {featured.length > 0 && (
                <div>
                  <p className="text-[10.5px] font-heading font-bold uppercase tracking-[1.3px] text-primary mb-1">
                    All events
                  </p>
                  <h2 className="text-[15.5px] font-heading font-extrabold text-navy">Agency calendar</h2>
                </div>
              )}
              {renderGrid(rest, false)}
            </div>
          )}
        </>
      )}
    </div>
  );
}
